import React from 'react'
import { Switch, Route, Redirect, useLocation } from 'react-router-dom'
import { useAuth, UserRole, UserPermissions } from '@modmd/data'
import { Box } from 'components/Layout'
import { pxToRem } from 'theme'
import { ROUTES } from 'internal-portal/constants/routes'
import Companies from 'internal-portal/routes/Companies'
import Dashboard from 'internal-portal/routes/Dashboard'
import { CompanyDetail } from 'internal-portal/routes/CompanyDetail'
import { ResetPassword } from 'internal-portal/routes/ResetPassword'
import TestSessions from 'internal-portal/routes/TestSessions'
import { TestSessionDetails } from 'internal-portal/routes/TestSessionDetails'
import SignIn from 'internal-portal/routes/SignIn'
import { RequestResetPassword } from 'internal-portal/routes/RequestResetPassword'
import SignUp from 'internal-portal/routes/SignUp'
import { Users, SubpersonsList } from 'internal-portal/routes/Users'
import TestsList from 'internal-portal/routes/TestsList'
import EventsList from 'internal-portal/routes/EventsList'
import { FinancesList, DefaultPricesList, LocationList } from 'internal-portal/routes/FinancesList'
import EventDetail from 'internal-portal/routes/EventDetail'
import { TestDetails } from 'internal-portal/routes/TestDetails'
import { VaccineRecordVerification } from 'internal-portal/routes/VaccineRecordVerification'
import { InsuranceReviewVerification } from 'internal-portal/routes/InsuranceReviewVerification'
import { InsuranceErrorsPage } from 'internal-portal/routes/InsuranceErrors'
import { UserDetails } from 'internal-portal/routes/components/UserDetails'
import { UserProfile } from 'internal-portal/routes/UserProfile'
import RequestTestSession from 'internal-portal/routes/RequestTestSession'
import { DriveThroughLocations } from 'internal-portal/routes/DriveThroughLocations'
import { Loader } from 'components/Loader'
import AnalyticsAppUsage from 'internal-portal/routes/AnalyticsAppUsage'
import Analytics from 'internal-portal/routes/Analytics'
import AnalyticsTestsResults from 'internal-portal/routes/AnalyticsTestsResults'
import InsuranceAnalyticsTestsResults from 'internal-portal/routes/AnalyticsInsuranceTestsResults/InsuranceAnalyticsTestsResults'
import AnalyticsMarketing from 'internal-portal/routes/AnalyticsMarketing'
import EventAttendeesList from 'internal-portal/routes/EventAttendeesList'
import ClientSide from 'client-portal/App'
import ReportsLogs from 'internal-portal/routes/ReportsLogs'
import InsuranceCompanies from 'internal-portal/routes/InsuranceCompanies'
import UserLogs from 'internal-portal/routes/UserLogs'
import UnverifiedTestsList from './routes/TestsList/UnverifiedTestsList'

const App = () => {
  const location = useLocation<{ from: string }>()
  const { isLoggedIn, isLoading, hasRoles, hasPermission } = useAuth()
  const isPatient = hasRoles([UserRole.PATIENT])

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={pxToRem(300)}>
        <Loader />
      </Box>
    )
  }
  if (isLoggedIn) {
    if (isPatient) return <ClientSide />
    return (
      <Switch>
        <Route exact path={`/${ROUTES.DASHBOARD}`} component={Dashboard} />
        {hasPermission([
          UserPermissions.SEE_FULL_TEST_SESSIONS,
          UserPermissions.SEE_GROUP_COMPANY_TEST_SESSIONS,
          UserPermissions.SCHEDULE_TEST_SESSION_ALL_USERS,
          UserPermissions.SEE_FINISHED_TEST_SESSIONS,
          UserPermissions.SEE_FINISHED_TEST_SESSIONS_GROUP_ASSIGNED,
          UserPermissions.SEE_STAFFED_TEST_SESSION,
        ]) && <Route exact path={`/${ROUTES.SESSIONS}`} component={TestSessions} />}
        {hasPermission([
          UserPermissions.SEE_FULL_LIST_TESTS,
          UserPermissions.SEE_FULL_COMPLETED_TESTS,
          UserPermissions.SEE_GROUP_COMPANY_LIST_TESTS,
        ]) && <Route exact path={`/${ROUTES.TESTS}`} component={TestsList} />}
        {hasPermission([
          UserPermissions.SEE_FULL_LIST_TESTS,
          UserPermissions.SEE_GROUP_COMPANY_LIST_TESTS,
        ]) && <Route exact path={`/${ROUTES.UNVERIFIED_TESTS}`} component={UnverifiedTestsList} />}
        {hasPermission([UserPermissions.SEE_EVENTS, UserPermissions.SCHEDULE_EVENT]) && (
          <Route exact path={`/${ROUTES.EVENTS}`} component={EventsList} />
        )}
        {hasPermission([UserPermissions.SEE_EVENT_ATTENDEE_LIST]) && (
          <Route exact path={`/${ROUTES.EVENT_ATTENDEES}`} component={EventAttendeesList} />
        )}
        {hasPermission([UserPermissions.SEE_EVENTS]) && (
          <Route exact path={`/${ROUTES.EVENTS}/:eventId`} component={EventDetail} />
        )}
        {hasPermission([UserPermissions.SEE_VACCINE_RECORD_VERIFICATION]) && (
          <Route
            exact
            path={`/${ROUTES.VACCINE_RECORD_VERIFICATION}`}
            component={VaccineRecordVerification}
          />
        )}
        {hasPermission([UserPermissions.SEE_INSURANCE_REVIEW_VERIFICATION]) && (
          <Route
            exact
            path={`/${ROUTES.INSURANCE_REVIEW_VERIFICATION}`}
            component={InsuranceReviewVerification}
          />
        )}
        {hasPermission([UserPermissions.SEE_EDIT_INSURANCE_COMPANIES]) && (
          <Route exact path={`/${ROUTES.INSURANCE_COMPANIES}`} component={InsuranceCompanies} />
        )}
        {hasPermission([UserPermissions.SEE_EDIT_INSURANCE_COMPANIES]) && (
          <Route exact path={`/${ROUTES.INSURANCE_ERROR_LOGS}`} component={InsuranceErrorsPage} />
        )}
        {hasPermission([UserPermissions.SEE_LIST_COMPANIES]) && (
          <Route exact path={`/${ROUTES.COMPANIES}`} component={Companies} />
        )}
        {hasPermission([UserPermissions.SEE_ANONAMYZED_ANALYTICS]) && (
          <Route exact path={`/${ROUTES.ANALYITCS}`} component={Analytics} />
        )}
        {hasPermission([UserPermissions.SEE_ANALYTICS_TEST_RESULTS]) && (
          <Route
            exact
            path={`/${ROUTES.ANALYTICS_TESTS_RESULTS}`}
            component={AnalyticsTestsResults}
          />
        )}
        {hasPermission([UserPermissions.SEE_ANALYTICS_TEST_RESULTS]) && (
          <Route
            exact
            path={`/${ROUTES.ANALYTICS_INSURANCE_TESTS}`}
            component={InsuranceAnalyticsTestsResults}
          />
        )}
        {hasPermission([UserPermissions.SEE_ANALYTICS_APP_USAGE]) && (
          <Route exact path={`/${ROUTES.ANALYTICS_APP_USAGE}`} component={AnalyticsAppUsage} />
        )}
        {hasPermission([UserPermissions.SEE_ANALYTICS_OPTIN_MARKETING]) && (
          <Route exact path={`/${ROUTES.ANALYTICS_MARKETING}`} component={AnalyticsMarketing} />
        )}
        {hasPermission([UserPermissions.SEE_ALL_USERS]) && (
          <Route exact path={`/${ROUTES.USERS}`} component={Users} />
        )}
        {hasPermission([UserPermissions.SEE_PATIENTS]) && (
          <Route exact path={`/${ROUTES.USERS}/patient`} component={Users} />
        )}
        {hasPermission([UserPermissions.SEE_SUBPERSONS]) && (
          <Route exact path={`/${ROUTES.USERS}/subperson`} component={SubpersonsList} />
        )}
        {hasPermission([UserPermissions.SEE_COMPANY_OWNERS]) && (
          <Route exact path={`/${ROUTES.USERS}/companyowner`} component={Users} />
        )}
        {hasPermission([UserPermissions.SEE_STAFF]) && (
          <Route exact path={`/${ROUTES.USERS}/staff`} component={Users} />
        )}
        {hasPermission([UserPermissions.SEE_USER_PROFILE]) && (
          <Route exact path={`/${ROUTES.USER_PROFILE}`} component={UserProfile} />
        )}
        {hasPermission([
          UserPermissions.SEE_FULL_TEST_SESSIONS,
          UserPermissions.SEE_GROUP_COMPANY_TEST_SESSIONS,
          UserPermissions.SEE_GROUP_COMPANY_DASHBOARD,
          UserPermissions.FINISH_TEST_SESSION,
          UserPermissions.FINISH_TEST_SESSION_SESSIONS_ASSIGNED,
          UserPermissions.SEE_FINISHED_TEST_SESSIONS,
          UserPermissions.SEE_FINISHED_TEST_SESSIONS_GROUP_ASSIGNED,
          UserPermissions.SEE_STAFFED_TEST_SESSION,
        ]) && (
          <Route exact path={`/${ROUTES.SESSIONS}/:testSessionId`} component={TestSessionDetails} />
        )}
        {hasPermission([
          UserPermissions.SEE_ALL_USERS_INFORMATION,
          UserPermissions.SEE_ALL_USERS,
          UserPermissions.SEE_PATIENTS,
          UserPermissions.SEE_SUBPERSONS,
          UserPermissions.SEE_COMPANY_OWNERS,
          UserPermissions.SEE_STAFF,
        ]) && <Route exact path={`/${ROUTES.USERS}/:userId`} component={UserDetails} />}
        {hasPermission([UserPermissions.SEE_LIST_COMPANIES]) && (
          <Route exact path={`/${ROUTES.COMPANIES}/:companyId`} component={CompanyDetail} />
        )}
        {hasPermission([
          UserPermissions.SEE_FULL_LIST_TESTS,
          UserPermissions.SEE_FULL_COMPLETED_TESTS,
          UserPermissions.SEE_GROUP_COMPANY_LIST_TESTS,
          UserPermissions.RELEASE_TEST_RESULTS,
        ]) && <Route exact path={`/${ROUTES.TESTS}/:testId`} component={TestDetails} />}
        {hasPermission([
          UserPermissions.SEE_FULL_LIST_TESTS,
          UserPermissions.SEE_FULL_COMPLETED_TESTS,
          UserPermissions.SEE_GROUP_COMPANY_LIST_TESTS,
          UserPermissions.RELEASE_TEST_RESULTS,
        ]) && <Route exact path={`/${ROUTES.UNVERIFIED_TESTS}/:testId`} component={TestDetails} />}
        {hasPermission([UserPermissions.SCHEDULE_TEST_SESSION_ALL_USERS]) && (
          <Route exact path={`/${ROUTES.REQUEST_TEST_SESSION}`} component={RequestTestSession} />
        )}
        {hasPermission([
          UserPermissions.SCHEDULE_TEST_SESSION_ALL_USERS,
          UserPermissions.SCHEDULE_TEST_SESSION_GROUP_COMPANY_USERS,
          UserPermissions.SCHEDULE_TEST_SESSION_ASSIGNED_GROUP_COMPANY_USERS,
        ]) && (
          <Route
            exact
            path={`/${ROUTES.COMPANIES}/:companyId/${ROUTES.REQUEST_TEST_SESSION}/:groupId`}
            component={RequestTestSession}
          />
        )}
        {hasPermission([UserPermissions.SEE_FULL_DASHBOARD]) && (
          <Route
            exact
            path={`/${ROUTES.DRIVE_THROUGH_LOCATIONS}`}
            component={DriveThroughLocations}
          />
        )}
        {hasPermission([UserPermissions.SEE_FULL_DASHBOARD]) && (
          <Route exact path={`/${ROUTES.REPORTS_LOGS}`} component={ReportsLogs} />
        )}
        {hasPermission([UserPermissions.SEE_USER_LOGS]) && (
          <Route exact path={`/${ROUTES.USER_LOGS}`} component={UserLogs} />
        )}
        {hasPermission([UserPermissions.SEE_FINANCES]) && (
          <Route exact path={`/${ROUTES.FINANCES}`} component={FinancesList} />
        )}
        {hasPermission([UserPermissions.SEE_FINANCES]) && (
          <Route exact path={`/${ROUTES.FINANCES_PRICES}`} component={DefaultPricesList} />
        )}
        {hasPermission([UserPermissions.SEE_FINANCES]) && (
          <Route exact path={`/${ROUTES.FINANCES_LOCATIONS}`} component={LocationList} />
        )}
        <Redirect to={`/${ROUTES.DASHBOARD}`} />
      </Switch>
    )
  }
  return (
    <Switch>
      <Route exact path="/" component={SignIn} />
      <Route exact path={`/${ROUTES.SIGN_UP}/:token`} component={SignUp} />
      <Route exact path={`/${ROUTES.REQUEST_PASSWORD_RESET}`} component={RequestResetPassword} />
      <Route exact path={`/${ROUTES.RESET_PASSWORD}/:token`} component={ResetPassword} />
      <Redirect
        to={{
          pathname: '/',
          state: {
            from: `${location.pathname}${location.search}`,
          },
        }}
      />
    </Switch>
  )
}

export default App
